/**
 * O mapa da casa.
 *
 * **O ponto e o dado, a idade e a promessa.** O item 6 pede "ponto antigo nunca
 * apresentado como atual", e a tela inteira gira em torno disso: a idade vem
 * calculada do servidor (`idade_s`), aparece no rotulo do marcador, na lista de
 * pessoas e no popup, e **muda o desenho** quando passa de uma hora. Um ponto de
 * tres horas atras que aparece com a mesma cor de um de trinta segundos diz "a Ana
 * esta aqui" quando a Ana esta em outro estado ha tres horas -- e e essa leitura
 * que faz alguem ligar para o hospital.
 *
 * **A lista e a fonte da verdade; o mapa e a mesma coisa desenhada.** Os dois leem
 * a mesma estrutura, entao nao podem discordar sobre a idade de um ponto. Se
 * divergissem, o item estaria entregue pela metade sem nenhum teste falhar.
 *
 * **A stream fecha quando a tela sai**, e o `remove()` do Leaflet vem junto: cada
 * stream segura uma conexao do pool da API, e um mapa vazio ficaria no DOM
 * consumindo tiles invisiveis.
 *
 * **O `popup` escapa com `texto()`**, como todo o resto da tela: o rotulo do
 * aparelho vem do banco, e o semeador cria um aparelho cujo rotulo tem marcacao
 * justamente para isto.
 */

import {
  ErroDaApi,
  aparelhos as buscarAparelhos,
  historicoDePedidos,
  pedirPosicao,
  pausarCompartilhamento,
} from "./api";
import type { Aparelho, PedidoHistorico, Ponto, Retrato } from "./api";
import { comReautenticacao } from "./reauth";
import { abrirStream } from "./sse";
import {
  idadeLegivel,
  painel,
  telaDoMapa,
  texto,
  quando,
} from "./telas";
import type { PedidoParaTela, PessoaParaTela } from "./telas";

/**
 * Leaflet entra como dependencia de runtime -- a **primeira** do aplicativo.
 *
 * **`circleMarker`, e nao o pino padrao.** O pino padrao e uma imagem que o CSS
 * do Leaflet referencia por caminho relativo, e caminho relativo dentro de um
 * bundle quebra: seria preciso reimportar as tres PNGs e reescrever as URLs. O
 * circulo **nao tem imagem nenhuma** e, o que importa mais aqui, **a cor e
 * dado**: um circulo vermelho e um ponto de tres horas atras, e o item 6 e
 * sobre a idade.
 */
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

/** Os cinco eventos da stream de localizacao. O `snapshot` abre a tela. */
const EVENTOS = ["snapshot", "location", "request_answered", "request_expired", "requests_expired"];

/**
 * O tiles sao do OpenStreetMap, e a **atribuicao e obrigatoria** -- nao um detalhe
 * de Licença: quem usa o mapa de outra pessoa precisa saber de onde ele veio. A
 * string passa pelo Leaflet como HTML, e ela e **nossa**, com URL fixa: nao ha
 * dado de ninguem dentro dela.
 */
const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATRIBUICAO = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const VERMELHO = "#b3261e";
const VERDE = "#1b7f4b";

let raiz: HTMLElement | null = null;
let mapa: L.Map | null = null;
let camada: L.LayerGroup | null = null;
let fecharStream: (() => void) | null = null;

/** **Um ponto por aparelho**: a chave e o aparelho, e o valor o ultimo ponto. */
const pontos = new Map<string, Ponto>();
let meus: Aparelho[] = [];
let historico: PedidoHistorico[] = [];

/**
 * Os aparelhos com **pedido vivo**, e o `sms:` de cada um quando a resposta
 * precisa passar pelo iPhone.
 *
 * Sao dois mapas e nao um porque os dois tem prazos diferentes: o pedido expira
 * sozinho em 120 s e a stream avisa; o link da mensagem e da ultima pergunta e
 * **some quando a pessoa pede de novo**, porque o telefone le o link uma vez.
 */
const pedidosVivos = new Set<string>();
const linksDeMensagem = new Map<string, string>();

export function definirRaiz(elemento: HTMLElement): void {
  raiz = elemento;
}

/** Fecha a stream e **destroi o mapa**. Chamado quando a pessoa sai da tela. */
export function fechar(): void {
  fecharStream?.();
  fecharStream = null;
  if (mapa !== null) {
    // **`remove()` e obrigatorio, e nao `off()`**: ele tira o container do Leaflet
    // do DOM e solta os ouvintes. `off()` deixaria o mapa vivo sobre um elemento
    // que o proximo `innerHTML` vai substituir.
    mapa.remove();
    mapa = null;
  }
  camada = null;
  pontos.clear();
  meus = [];
  historico = [];
  pedidosVivos.clear();
  linksDeMensagem.clear();
}

/** Desenha a tela inteira. **Uma vez por visita** -- quem repinta e `repinta`. */
export async function desenhar(): Promise<void> {
  if (raiz === null) {
    return;
  }
  await recarrega();

  const caixa = painel(telaDoMapa(pessoasParaTela(), pedidosParaTela()));
  raiz.innerHTML = caixa;
  raiz.classList.add("conteudo--centro");

  const area = raiz.querySelector<HTMLElement>("#mapa");
  if (area !== null) {
    mapa = L.map(area, { attributionControl: true });
    L.tileLayer(TILES, { attribution: ATRIBUICAO, maxZoom: 19 }).addTo(mapa);
    camada = L.layerGroup().addTo(mapa);
  }
  centraliza();
  desenhaPinos();
  repinta();
}

async function recarrega(): Promise<void> {
  const [lista, pedidos] = await Promise.all([buscarAparelhos(), historicoDePedidos()]);
  meus = lista;
  historico = pedidos;
}

/**
 * Repinta **as listas e os pinos**, sem mexer no container do mapa.
 *
 * **O `innerHTML` do casco inteiro nao pode rodar aqui.** Ele destruiria o
 * elemento que o Leaflet esta usando, e o mapa perderia a camada e a posicao --
 * voltando a "agora" na primeira atualizacao. E o `repinta` quem religa os botoos,
 * porque botao recriado nao tem ouvinte.
 */
function repinta(): void {
  if (raiz === null) {
    return;
  }
  const listaDePessoas = raiz.querySelector<HTMLElement>("#lista-de-pessoas");
  const listaDePedidos = raiz.querySelector<HTMLElement>("#lista-de-pedidos");
  if (listaDePessoas === null || listaDePedidos === null) {
    return;
  }
  const shell = telaDoMapa(pessoasParaTela(), pedidosParaTela());
  listaDePessoas.innerHTML = extrai(shell, "lista-de-pessoas");
  listaDePedidos.innerHTML = extrai(shell, "lista-de-pedidos");
  liga();
}

/**
 * Pega so a `<ul>` de dentro do HTML da tela.
 *
 * **Um `id` no HTML da tela e a lista; o resto e o que o `repinta` troca.** Sem
 * isto, o `repinta` teria de reescrever as duas listas a mao -- e a versao escrita
 * a mao e a que diverge da versao do primeiro desenho depois de uma mudanca.
 */
function extrai(html: string, id: string): string {
  const achado = html.indexOf(`id="${id}"`);
  if (achado < 0) {
    return "";
  }
  // **`lastIndexOf`, e nao `indexOf`.** O `id` vem *depois* do `<ul` no HTML, e o
  // `indexOf` acharia o `<ul` seguinte -- a lista de pedidos, e nao a de
  // pessoas. O resultado seria a tela trocando uma lista pela outra, sem erro e
  // sem nenhuma excecao.
  const abre = html.lastIndexOf("<ul", achado);
  const fecha = html.indexOf("</ul>", achado);
  return abre < 0 || fecha < 0 ? "" : html.slice(html.indexOf(">", abre) + 1, fecha);
}

/** As pessoas da lista: **os meus aparelhos e quem tem ponto**, sem repetir. */
function pessoasParaTela(): PessoaParaTela[] {
  const ids = new Set<string>([...meus.map((a) => a.id), ...pontos.keys()]);
  const pessoas: PessoaParaTela[] = [];
  for (const id of ids) {
    const ponto = pontos.get(id);
    const aparelho = meus.find((a) => a.id === id);
    const idade = ponto === undefined ? null : idadeLegivel(ponto.idade_s);
    pessoas.push({
      device_id: id,
      nome: ponto?.dono_nome ?? aparelho?.label ?? "Aparelho",
      rotulo: ponto?.device_label ?? aparelho?.label ?? "",
      idade: idade?.texto ?? "",
      velho: idade?.velho ?? false,
      precisao:
        ponto === undefined || ponto.accuracy_meters === null
          ? "?"
          : `${Math.round(ponto.accuracy_meters)} m`,
      // **`null` e bateria desconhecida, e vira `?`** -- nunca `0%`. Um `0` seria
      // lido como "o celular esta morrendo", e a pessoa trocaria a bateria de um
      // aparelho que apenas nao reporta o nivel.
      bateria: ponto === undefined || ponto.battery_percent === null ? "?" : `${ponto.battery_percent}%`,
      coletado: ponto === undefined ? "" : quando(ponto.recorded_at),
      meu: aparelho !== undefined,
      pausado: aparelho?.compartilhamento_pausado ?? false,
      semPosicao: ponto === undefined,
      // **O pedido vivo vem do retrato da stream**, que e a verdade do servidor.
      // O clique tambem marca aqui, para a tela responder na hora -- mas quem
      // decide o que continua esperando depois de um minuto e a stream.
      pedindo: pedidosVivos.has(id),
      linkDaMensagem: linksDeMensagem.get(id) ?? null,
    });
  }
  return pessoas;
}

/**
 * Os pedidos: **"quem pediu e quando"**, dos dois lados do historico.
 *
 * O historico ja vem filtrado pela API (8.210) -- o que a tela nao faz e filtrar
 * de novo, e sim dizer de que lado cada linha esta.
 */
function pedidosParaTela(): PedidoParaTela[] {
  const meusIds = new Set(meus.map((a) => a.id));
  return historico.map((linha) => ({
    id: linha.id,
    quem: linha.solicitante_nome === "" ? "Alguem" : linha.solicitante_nome,
    sobre: linha.device_label,
    quando: quando(linha.requested_at),
    desfecho: desfechoDe(linha),
    sobreMeu: meusIds.has(linha.device_id),
  }));
}

/** Os tres desfechos, em palavras. `pending` e o unico que ainda nao aconteceu. */
function desfechoDe(linha: PedidoHistorico): string {
  if (linha.status === "answered") {
    return linha.answered_location_id === null ? "respondeu sem coordenadas" : "atendeu";
  }
  if (linha.status === "expired") {
    return "expirou sem resposta";
  }
  return "esperando";
}

/** Liga os botoes de pausa. **Toda vez que a lista e repintada.** */
function liga(): void {
  if (raiz === null) {
    return;
  }
  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-pausar]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["pausar"] ?? "";
      const pausado = botao.dataset["pausado"] === "sim";
      // **O botao so existe no aparelho da propria pessoa** (`data-pessoa` so
      // ganha `meu` quando o aparelho esta em `meus`), e a API recusa o aparelho
      // alheio com `404`. A tela e a API concordam porque as duas conferem a
      // mesma coisa: quem e o dono.
      void executa(async () => {
        await comReautenticacao(async () => {
          // **A resposta e a fonte do novo estado do botao**, e nao o valor que
          // pedimos: se a API recusasse e devolvesse o velho, o interruptor
          // mentiria.
          const atualizado = await pausarCompartilhamento(id, !pausado);
          meus = meus.map((a) => (a.id === atualizado.id ? atualizado : a));
        });
        repinta();
      });
    });
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-pedir]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["pedir"] ?? "";
      // **Sem `comReautenticacao`, e nao por esquecimento:** pedir a posicao de
      // alguem e uma **acao**, nao a leitura de um segredo. E o que a rota de
      // pedidos decidiu (PLANO 8.146), e uma digrada de senha a cada "onde esta
      // o Fulano" seria um preco maior que o da propria pergunta.
      void executa(async () => {
        const resposta = await pedirPosicao(id);
        // **O `sms:` e o do servidor, nunca montado aqui.** O telefone esta no
        // cadastro do aparelho e a frase-codigo no pedido, e nenhum dos dois
        // esta no estado da tela: uma URL montada aqui sairia com o telefone
        // errado, e o telefone errado manda a pergunta para a pessoa errada.
        linksDeMensagem.delete(id);
        if (resposta.sms_para !== null) {
          linksDeMensagem.set(id, resposta.sms_para);
        }
        pedidosVivos.add(id);
        repinta();
      });
    });
  }
}

/** Desenha os pinos. Um `circleMarker` por aparelho, colorido pela idade. */
function desenhaPinos(): void {
  if (mapa === null || camada === null) {
    return;
  }
  camada.clearLayers();
  for (const ponto of pontos.values()) {
    const idade = idadeLegivel(ponto.idade_s);
    const cor = idade.velho ? VERMELHO : VERDE;
    L.circleMarker([ponto.lat, ponto.lon], {
      radius: 9,
      color: cor,
      fillColor: cor,
      fillOpacity: 0.75,
      weight: 2,
    })
      .bindPopup(popupDe(ponto, idade.texto))
      .addTo(camada);
  }
}

/**
 * O popup. **Tudo por `texto()`** -- o nome e o rotulo vem do banco, e o popup e
 * `innerHTML` tanto quanto o resto da tela.
 */
function popupDe(ponto: Ponto, idade: string): string {
  return [
    `<strong>${texto(ponto.dono_nome)}</strong>`,
    texto(ponto.device_label),
    `coletado ${texto(quando(ponto.recorded_at))} (${texto(idade)})`,
    `precisao ${texto(ponto.accuracy_meters === null ? "?" : `${Math.round(ponto.accuracy_meters)} m`)}`,
    `bateria ${texto(ponto.battery_percent === null ? "?" : `${ponto.battery_percent}%`)}`,
  ].join("<br>");
}

/** Centraliza nos pontos. **Sem ponto, o mapa abre no mundo inteiro.** */
function centraliza(): void {
  if (mapa === null) {
    return;
  }
  if (pontos.size === 0) {
    mapa.setView([0, 0], 2);
    return;
  }
  const limites = L.latLngBounds([...pontos.values()].map((p) => [p.lat, p.lon] as [number, number]));
  mapa.fitBounds(limites, { padding: [40, 40], maxZoom: 15 });
}

/**
 * Abre a stream. **Uma vez por visita**, e nao a cada repintada.
 */
export function abrir(): void {
  fecharStream = abrirStream("/api/location/stream", EVENTOS, (dados) => {
    void aoEvento(dados as unknown as Record<string, unknown>).catch(() => undefined);
  });
}

/**
 * O que fazer com um evento.
 *
 * **`snapshot` substitui tudo; `location` substitui um aparelho.** Um tique pode
 * trazer varios pontos, e o retrato e o estado inicial inteiro -- tratá-los igual
 * deixaria o aparelho que nao mandou nada naquele tique com o ponto antigo, que e
 * o comportamento certo: um ponto e a **ultima posicao conhecida**, nao a atual.
 */
async function aoEvento(dados: Record<string, unknown>): Promise<void> {
  if (dados === null || dados === undefined) {
    return;
  }
  if (Array.isArray(dados["pontos"])) {
    const retrato = dados as unknown as Retrato;
    pontos.clear();
    for (const ponto of retrato.pontos) {
      pontos.set(ponto.device_id, ponto);
    }
    // **O retrato traz os pedidos que continuam esperando**, e isso e quem decide
    // o selo "esperando resposta". O clique marca na hora, mas quem manda na
    // verdade depois de um minuto e o servidor.
    pedidosVivos.clear();
    for (const pedido of retrato.pedidos) {
      pedidosVivos.add(pedido.device_id);
    }
    centraliza();
    desenhaPinos();
    repinta();
    return;
  }
  if (dados["device_id"] !== undefined && dados["lat"] !== undefined) {
    const ponto = dados as unknown as Ponto;
    pontos.set(ponto.device_id, ponto);
    desenhaPinos();
    repinta();
    return;
  }
  // **Um pedido que mudou de estado so recarrega o historico.** O evento traz o
  // pedido, e nao traz **quem** pediu -- e sem nome, "quem pediu sua posicao e
  // quando" nao tem resposta.
  if (dados["id"] !== undefined || dados["ids"] !== undefined) {
    // **O selo "esperando" sai junto.** O aparelho respondeu (ou nao), e a tela
    // que continuasse mostrando "esperando resposta" depois de um `answered`
    // seria a tela discordando de um evento que ela mesma recebeu.
    if (typeof dados["device_id"] === "string") {
      pedidosVivos.delete(dados["device_id"]);
      linksDeMensagem.delete(dados["device_id"]);
    }
    historico = await historicoDePedidos();
    repinta();
  }
}

/** Mostra o erro no mapa, sem derrubar a tela. */
async function executa(acao: () => Promise<void>): Promise<void> {
  try {
    await acao();
  } catch (erro) {
    const area = raiz?.querySelector<HTMLElement>("#aviso-do-mapa");
    if (area === null || area === undefined) {
      return;
    }
    area.hidden = false;
    area.textContent = erro instanceof ErroDaApi ? erro.message : "Algo deu errado.";
  }
}